import { FiltersContextProvider } from '@openx/components-filters/lib/context/FiltersContext';
import type { FiltersContextProviderProps } from '@openx/components-filters/lib/context/types';
import { Table, TableName } from '@openx/components-table/Table';
import type { InventoryListByIdAPI, InventoryListsListFilters } from '@openx/types/inventoryList';
import type { AllTableCriteria, CriteriaChange, CriteriaDimension } from '@openx/types/tableCriteria';
import { useCallback } from 'react';
import { InventoryListsTopBar } from './InventoryListsTopBar';
import { useFiltersConfig } from './useFiltersConfig';
import { useInventoryListsTableConfig } from './useInventoryListsTableConfig';

type InventoryListsListProps = {
  criteria: AllTableCriteria<InventoryListsListFilters>;
  setCriteria: (change: CriteriaChange<CriteriaDimension, InventoryListsListFilters>) => void;
  inventoryLists: InventoryListByIdAPI[];
  loading: boolean;
};

export const InventoryListsList = ({ criteria, inventoryLists, loading, setCriteria }: InventoryListsListProps) => {
  const { rowActions, columns } = useInventoryListsTableConfig(criteria.filters?.phrase || '');
  const filtersConfig = useFiltersConfig();

  const onFiltersChange = useCallback(
    (value: InventoryListsListFilters) => {
      setCriteria({ dimension: 'filters', value });
    },
    [setCriteria]
  );

  const filtersProviderConfig: FiltersContextProviderProps<InventoryListsListFilters>['config'] = {
    filtersConfig,
    visibleFilters: ['account_uid'],
    criteria,
    disabledFilters: [],
    onFiltersChange,
  };

  return (
    <FiltersContextProvider config={filtersProviderConfig}>
      <InventoryListsTopBar />

      <Table<InventoryListByIdAPI, InventoryListsListFilters>
        name={TableName.INVENTORY_LISTS}
        criteria={criteria}
        data={inventoryLists}
        rowActions={rowActions}
        onCriteriaChange={setCriteria}
        columns={columns}
        loading={loading}
      />
    </FiltersContextProvider>
  );
};
